import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import useGlobalSettings from '@/src/GlobalSettings/hooks/useGlobalSettings';

/*
 * Global settings — the system gallery base URLs every org falls back
 * to when no whitelabel domain applies. Two fields; the API returns
 * snake_case and accepts camelCase (preserved asymmetry).
 */

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function AdminGlobalSettingsV2() {
    const { getGlobalSettings, updateGlobalSettings } = useGlobalSettings();

    const [baseUrl, setBaseUrl] = useState('');
    const [alphaUrl, setAlphaUrl] = useState('');
    const [saved, setSaved] = useState({ baseUrl: '', alphaUrl: '' });
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;
        getGlobalSettings().then((settings) => {
            if (!mounted || !settings) {
                setIsLoading(false);
                return;
            }
            const base = settings.system_gallery_base_url ?? '';
            const alpha = settings.system_gallery_alpha_url ?? '';
            setBaseUrl(base);
            setAlphaUrl(alpha);
            setSaved({ baseUrl: base, alphaUrl: alpha });
            setUpdatedAt(settings.updated_at ?? null);
            setIsLoading(false);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dirty =
        baseUrl.trim() !== saved.baseUrl ||
        alphaUrl.trim() !== saved.alphaUrl;
    const valid =
        /^https?:\/\/.+/.test(baseUrl.trim()) &&
        /^https?:\/\/.+/.test(alphaUrl.trim());

    const save = async () => {
        if (!dirty || !valid || saving) return;
        setSaving(true);
        const updated = await updateGlobalSettings({
            systemGalleryBaseUrl: baseUrl.trim(),
            systemGalleryAlphaUrl: alphaUrl.trim(),
        });
        setSaving(false);
        if (updated) {
            setSaved({
                baseUrl: baseUrl.trim(),
                alphaUrl: alphaUrl.trim(),
            });
            setUpdatedAt(updated.updated_at ?? null);
            toast.success('Saved');
        } else {
            toast.error("Couldn't save — try again.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <SettingsCard
            title="Gallery base URLs"
            desc="Every gallery link falls back to these when the org has no whitelabel domain. Changing them changes live links immediately."
            foot={
                <>
                    <span className="flex-1 text-[11.5px] text-faint">
                        {!valid
                            ? 'both must be full URLs (https://…)'
                            : updatedAt
                            ? `last updated ${moment(updatedAt).fromNow()}`
                            : ' '}
                    </span>
                    <button
                        type="button"
                        disabled={!dirty || !valid || saving}
                        onClick={save}
                        className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background disabled:opacity-40"
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </>
            }
        >
            <div className="max-w-[440px]">
                <label className={FIELD_LABEL}>Production gallery URL</label>
                <input
                    className="ps-input mb-3 w-full font-mono text-[12px]"
                    placeholder="https://smlbth.io"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                />
                <label className={FIELD_LABEL}>Alpha gallery URL</label>
                <input
                    className="ps-input w-full font-mono text-[12px]"
                    placeholder="https://gallery.alpha.smilebooth.com"
                    value={alphaUrl}
                    onChange={(e) => setAlphaUrl(e.target.value)}
                />
            </div>
        </SettingsCard>
    );
}
